import { StringTMap } from '@@types/common';

import { isNonEmpty, isString, mapIndexed } from '~/utils/fnUtil/fnUtil';
import { always, curry, defaultTo, evolve, forEach, groupBy, ifElse, map, pipe, values } from 'ramda';

class FetchCache<Key extends string, Type> {
  private cache: StringTMap<Promise<Type>> = {};
  keyMap: StringTMap<boolean> = {};

  public wrapFetch = curry((key: Key, fetch: (key: Key) => Promise<Type>): Promise<Type> => {
    if (!this.getPromise(key)) {
      this.setPromiseMap(
        key,
        fetch(key).finally(() => this.removePromise(key))
      );
    }
    return this.getPromise(key);
  });

  public wrapFetchList = (keyList: Key[], fetch: (keyList: Key[]) => Promise<Type[]>): Promise<Type[]> => {
    return pipe<any, any, any, any, any, any>(
      map(this.getPromiseOrKey),
      groupBy(ifElse(isString, always('keyList'), always('promiseList'))),
      evolve({
        keyList: ifElse(isNonEmpty, this.fetchListAndCache(fetch), () => Promise.resolve(null)),
        promiseList: (promiseList: Promise<Type>[] = []) => {
          return Promise.all(promiseList);
        },
      }),
      values,
      (promiseList: Promise<Type[]>[]) => Promise.all(promiseList)
    )(keyList);
  };

  private getPromiseOrKey = (key: Key): Promise<Type> | Key => {
    return pipe(this.getPromise, defaultTo(key))(key);
  };

  private fetchListAndCache = curry((fetch: (keyList: Key[]) => Promise<Type[]>, keyList: Key[]): Promise<Type[]> => {
    forEach((key) => {
      this.keyMap[key] = true;
    }, keyList);
    const promise = fetch(keyList);
    mapIndexed(
      (key: Key, index: number) =>
        this.setPromiseMap(
          key,
          promise.finally(() => this.removePromise(key)).then((list) => list[index])
        ),
      keyList
    );
    return promise;
  });

  private getPromise = (key: Key): Promise<Type> | undefined => {
    return this.cache[key];
  };

  private setPromiseMap = (key: Key, promise: Promise<Type>) => {
    this.cache[key] = promise;
  };

  private removePromise = (key: Key) => {
    delete this.cache[key];
  };
}

export default FetchCache;
